// MyLikesUIFunctions.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import FollowButtonInline from "../../components/FollowButtonInline";
import { useTranslation } from "react-i18next";
import { HLSPlayer } from "../Gallery/GalleryUIFunctions";
import { useHlsPrefetch } from "../../../shared/utils/useHlsPrefetch";

/** ——— Types (same shape as Gallery to keep interop) ——— */
export interface MediaEntry {
  url?: string;
  isHls?: boolean;
  videoId?: string;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string;
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
}
 
export interface Post {
  _id: string;
  media: MediaEntry[];
  captions: string[];
  createdAt: string;
  likes: number;
  likedByUser?: boolean;
  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
  user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;
}


//Connection
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;

if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const API_ORIGIN = API_BASE
  .replace(/\/api(\/)?$/, '')
  .replace(/\/$/, '');


// Sizing — match Gallery’s square-ish media logic
const MEDIA_BOX = "calc(100vh - 50px)";
const MEDIA_HEIGHT = MEDIA_BOX;
const MAX_MEDIA_WIDTH = MEDIA_BOX;

const postCardStyle: React.CSSProperties = {
  background:
  'linear-gradient(145deg, rgba(6, 11, 23, 0.95), rgba(4, 7, 15, 0.95))',  
  padding: "15px 20px 0px 20px",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  color: "#888",
};

// Horizontal strip similar to Gallery’s media scroll container
const mediaRowContainerStyle: React.CSSProperties = {
   display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  overflowY: "hidden",
  gap: "2px",
  paddingTop: 6,
  marginLeft: "-3%",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  touchAction: "auto",
  overscrollBehaviorX: "none",
};


const seenPrefetch = new Set<string>();

const prefetchAsset = (url?: string, isHls?: boolean) => {
  if (!url || seenPrefetch.has(url)) return;

  if (isHls) {
    try {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = url;
      document.head.appendChild(link);
    } catch {}
  } else {
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.src = url;
  }

  seenPrefetch.add(url);
};


/** ——— Hook: useMediaOrientation ——— */
export function useMediaOrientation(
  url: string,
  isVideo: boolean,
  onResult: (isLandscape: boolean | null) => void
) {
  React.useEffect(() => {
    let alive = true;

    if (!url) {
      onResult(null);
      return;
    }     

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;

      const handle = () => {
        if (!alive) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        onResult(w && h ? w > h : null);
        cleanup();
      };
      const err = () => {
        if (alive) onResult(null);
        cleanup();
      };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", handle);
        v.removeEventListener("error", err);
      };

      v.addEventListener("loadedmetadata", handle);
      v.addEventListener("error", err);
      try { v.load(); } catch {}

      return () => {
        alive = false;
        cleanup();
      };
    } else {
      onResult(null);
    }
  }, [url, isVideo, onResult]);
}

/** ——— Component: ImageAutoFit ——— */
export function ImageAutoFit({
  src,
  alt,
  portraitStyle,
  landscapeStyle,
  outerPortraitStyle,
  outerLandscapeStyle,
  onOrientationChange,
  onClick,
  clickable,
  isPrepared,

}: {
  src: string;
  alt?: string;
  portraitStyle: React.CSSProperties;
  landscapeStyle: React.CSSProperties;
  outerPortraitStyle?: React.CSSProperties;
  outerLandscapeStyle?: React.CSSProperties;
  onOrientationChange?: (isLandscape: boolean | null) => void;
  onClick?: () => void;
  clickable?: boolean;
  isPrepared: boolean;
}) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      const val = w && h ? w > h : null;
      setIsLandscape(val);
      onOrientationChange?.(val ?? null);
    };
    img.onerror = () => {
      if (alive) {
        setIsLandscape(null);
        onOrientationChange?.(null);
      }
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src, onOrientationChange]);
  const { t } = useTranslation();
  const style = isLandscape ? landscapeStyle : portraitStyle;
  const outerStyle = isLandscape ? outerLandscapeStyle : outerPortraitStyle;

  return (
    <div style={outerStyle}>
     {isPrepared ? (
  <img
    src={src}
    alt={alt || t('gallery.media.previewAlt')}
    decoding="async"
    loading={isPrepared ? "eager" : "lazy"}
    style={{
      ...style,
      cursor: clickable ? "pointer" : undefined,
    }}
    onClick={onClick}
  />
) : (
  <div
    style={{
      ...style,
      background: "#050505",
    }}
  />
)}

    </div>
  );
}

/** ——— Component: MediaItem ——— */
export function MediaItem({
  postId,
  index,
  media,
  isActive,
  isPrepared,
  globalMuted,
  registerChild,
  openViewer,
  hoveredKey,
  handleHoverEnter,
  handleHoverLeave,
  onMusicClick,
}: {
  postId: string;
  index: number;
  media: MediaEntry;
  isActive: boolean;
  isPrepared: boolean;
  globalMuted: boolean;
  registerChild: (el: HTMLDivElement | null, i: number) => void;
  openViewer: (index: number) => void;
  hoveredKey: string | null;
  handleHoverEnter: (key: string) => void;
  handleHoverLeave: () => void;
  onMusicClick?: (entry: MediaEntry) => void;
}) {
 

const isVideo = media.isHls === true;
const [isVideoLandscape, setIsVideoLandscape] = React.useState<boolean | null>(null);
  
const navigate = useNavigate();

// 🔥 IMPORTANT: use HLS playlist like Gallery
const mediaUrl =
  isVideo
    ? (media.hlsUrl || (media.videoId ? `${API_ORIGIN}/hls/playlist/${media.videoId}` : ""))
    : (media.url || "");
 
  const { t } = useTranslation();

  const musicLabel = [media.musicTitle, media.musicArtist].filter(Boolean).join(" — ");

  
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);
// image orientation
useMediaOrientation(mediaUrl ?? "", false, (val) => {
  if (!isVideo) setIsLandscape(val);
});

  
  const hoverKey = `${postId}-${index}`;

  React.useEffect(() => {
  prefetchAsset(mediaUrl, isVideo);
}, [mediaUrl, media.isHls]);


  return (
 <div
    ref={(el) => registerChild(el, index)}
    onMouseEnter={() => handleHoverEnter(hoverKey)}
    onMouseLeave={handleHoverLeave}

    style={{
        position: "relative",
        height: MEDIA_HEIGHT,
        overflow: "visible",
        paddingBottom: 40,
        marginLeft: "3%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        scrollSnapAlign: "center",
        flex: "0 0 auto",
        width: "auto",
        maxWidth: MAX_MEDIA_WIDTH,
        boxSizing: "border-box",
      }}
    >

            {/* Media frame */}
  {/* Media frame OUTER — allows music to escape */}
<div
  style={{
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",

  }}
>

  {/* Media frame INNER — clips rounded corners */}
  <div
  style={{
    width: "100%",
    height: "100%",
    borderRadius: 12,
    overflow: "hidden",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
  }}
>

       {/* 🎬 VIDEO */}
{isVideo && (
  isVideoLandscape ? (
    // LANDSCAPE STRUCTURE
    <div
  style={{
    position: "relative",
    display: "inline-block",
    borderRadius: 12,
  }}
>
  
  <HLSPlayer
  src={mediaUrl}
  poster={media.posterUrl}
  muted={globalMuted}
  active={isActive}
  onOrientation={setIsVideoLandscape}
  isLandscape={true}
  style={{
          maxHeight: MEDIA_BOX,
          maxWidth: "100%",
          height: "auto",
          width: "auto",
          objectFit: "contain",
          borderRadius: 12,

        }}
      />

          {/* Music under video */}
 {musicLabel && hoveredKey === hoverKey && (
  <div
    onClick={(e) => {
      e.stopPropagation();

      if (media.musicId) {
        navigate(`/musicdetails/${media.musicId}`);
      } else {
        onMusicClick?.(media);
      }
    }}

   style={{
      position: "absolute",
      bottom: -22,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 11,
      color: "#807f7f",
      whiteSpace: "nowrap",
      cursor: "pointer",
      zIndex: 20,
      pointerEvents: "auto",
    }}
  >
    🎵 {musicLabel}
   </div>
 )}
     </div>
   ) : (
     // PORTRAIT STRUCTURE (original behavior)
     <>
       <HLSPlayer
         src={mediaUrl}
         muted={globalMuted}
         active={isActive}
         onOrientation={setIsVideoLandscape}
         isLandscape={false}
         style={{
           maxHeight: MEDIA_BOX,
           maxWidth: "100%",
           height: "auto",
           width: "auto",
           objectFit: "contain",
         }}
       />
 
 {musicLabel && hoveredKey === hoverKey && (
   <div
     onClick={(e) => {
       e.stopPropagation();
 
       if (media.musicId) {
         navigate(`/musicdetails/${media.musicId}`);
       } else {
         onMusicClick?.(media);
       }
     }}
     style={{
       position: "absolute",
       bottom: -20,
       left: "50%",
       transform: "translateX(-50%)",
       fontSize: 11,
       color: "#807f7f",
       whiteSpace: "nowrap",
       cursor: "pointer",
       zIndex: 6,
       pointerEvents: "auto",
     }}
   >
     🎵 {musicLabel}
   </div>
 )}
  
     </>
   )
 )}
 
        {!isVideo && mediaUrl && (

        <ImageAutoFit
          src={mediaUrl}
          alt={t('gallery.media.previewAlt')}
          portraitStyle={{
            // portrait: taller than wide (height = MEDIA_BOX)
            height: MEDIA_BOX,
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            borderRadius: 12,
          }}
          landscapeStyle={{
            // landscape: width == portrait height, height adapts
            width: MEDIA_BOX,
            height: "auto",
            maxHeight: "100%",
            maxWidth: "100%",
            objectFit: "contain",
            borderRadius: 12,
          }}
          outerPortraitStyle={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
          outerLandscapeStyle={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            paddingInline: 6,
          }}
          onOrientationChange={setIsLandscape}
          onClick={() => openViewer(index)}
          clickable
          isPrepared={isPrepared}
        />
         )}

     </div>
    </div>
    </div>
  );
}

/** ——— Component: PostSection ——— */
const PostSectionComponent = function PostSection({
  post,
  isActive,
  inWindow,
  globalMuted,
  openViewer,
  navigate,
  onLike,
  onUnlike,
  currentUserId,
  openPresentation,
  onMusicClick,
}: {
  post: Post;
  isActive: boolean;
  inWindow: boolean;
  globalMuted: boolean;
  openViewer: (media: MediaEntry[], index: number) => void;
  navigate: ReturnType<typeof useNavigate>;
  onLike: (id: string) => void;
  onUnlike: (id: string) => void;
  currentUserId?: string;
  openPresentation: (postId: string) => void;
  onMusicClick?: (entry: MediaEntry) => void;
}) {
  const ownerId = post.user?._id;
  const isOwnPost = !!ownerId && currentUserId === ownerId;
  const { t } = useTranslation();

React.useEffect(() => {
  return () => {
    seenPrefetch.clear();
  };
}, []);


const goChatWithAuthor = () => {
  if (!post.user?._id || isOwnPost) return;

  navigate("/chat", {
    state: {
      toUserId: post.user._id,
      toUserName: post.user.profilename || "",
      toUserPicture: post.user.picture || "",
    },
  });
};

  const goToAuthor = () => {
    if (!ownerId) return;
    navigate(isOwnPost ? "/profile" : `/visitor-profile/${ownerId}`);
  };
  const [currentIdx, setCurrentIdx] = React.useState(0);

    const nextMedia = post.media[currentIdx + 1];
const nextVideoId =
  nextMedia?.isHls && nextMedia.videoId
    ? nextMedia.videoId
    : null;

useHlsPrefetch(isActive ? nextVideoId : null);

React.useEffect(() => {
  if (isActive) {
    setCurrentIdx(0);
  }
}, [isActive]);

  // hover state for music pill
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
  const hoverTimeoutRef = React.useRef<number | null>(null);
  const handleHoverEnter = (key: string) => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredKey(key);
  };
  const handleHoverLeave = () => {
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredKey(null);
      hoverTimeoutRef.current = null;
    }, 1500);
  };
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  const containerRef = React.useRef<HTMLDivElement | null>(null);
const itemRefs = React.useRef<HTMLDivElement[]>([]);

const registerChild = (el: HTMLDivElement | null, i: number) => {
  if (el) itemRefs.current[i] = el;
};

React.useEffect(() => {
  const root = containerRef.current;
  if (!root) return;

  const io = new IntersectionObserver(
    (entries) => {
      let best: { idx: number; ratio: number } | null = null;

      for (const e of entries) {
        const idx = itemRefs.current.findIndex(n => n === e.target);
        if (idx >= 0) {
          const r = e.intersectionRatio;
          if (!best || r > best.ratio) best = { idx, ratio: r };
        }
      }

      if (best && best.ratio > 0) {
        requestAnimationFrame(() => setCurrentIdx(best!.idx));
      }
    },
    { root, threshold: [0, 0.25, 0.5, 0.75, 1] }
  );

  itemRefs.current.forEach(el => el && io.observe(el));
  return () => io.disconnect();
}, [post._id, post.media.length]);

  // header right side info (likedAt instead of postedAt)
 const countryCode = post.location?.countryCode?.toUpperCase();
const translatedCountry = countryCode
  ? t(`countries.${countryCode}`, {
      defaultValue: post.location?.country || countryCode,
    })
  : post.location?.country || "";

const locationLabel =
  post.location?.city && translatedCountry
    ? `${post.location.city}, ${translatedCountry}`
    : translatedCountry
    ? translatedCountry
    : "";

  const dateLabel = t("MyLikes.post.likedAt", {
    date: new Date(post.createdAt).toLocaleString(),
  });


  React.useEffect(() => {
  const next = post.media[currentIdx + 1];
  const prev = post.media[currentIdx - 1];

  if (next?.url) prefetchAsset(next.url, next.isHls);
  if (prev?.url) prefetchAsset(prev.url, prev.isHls);
}, [currentIdx, post.media]);


  return (
  <section
  data-post-id={post._id}
  style={{
    ...postCardStyle,
    height: "100vh",
    minHeight: "100vh",
    overflow: "hidden",
    boxSizing: "border-box",
    scrollSnapAlign: "start",
    scrollSnapStop: "always",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingTop: "15px",          // 👈 ADD THIS
    justifyContent: "flex-start" // 👈 ADD THIS
  }}
>

      {/* SINGLE HEADER ROW — match Gallery look */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginBottom: 1,
        }}
      >
        {/* LEFT: avatar + name + follow */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            onClick={goToAuthor}
            style={{
              width: 35,
              height: 35,
              borderRadius: "5%",
              backgroundColor: post.user?.picture ? "transparent" : "rgb(38, 29, 162)",
              backgroundImage: post.user?.picture ? `url(${post.user.picture})` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: post.user?.picture ? 0 : "20px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {!post.user?.picture && "👻"}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <span
              onClick={goToAuthor}
              style={{
                cursor: "pointer",
                color: "#bbb",
                fontWeight: 600,
                fontSize: 15,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: 220,
              }}
            >
              {post.user?.profilename || t("MyLikes.post.unknownUser")}
            </span>

            <span
  style={{
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    gap: 6,
  }}
>
  <FollowButtonInline
    authorId={post.user?._id}
    profilename={post.user?.profilename}
  />

  {!isOwnPost && (
    <button
      onClick={goChatWithAuthor}
      style={{
        backgroundColor: "#111",
        color: "#bbb",
        border: "1px solid #ccc",
        borderRadius: "6px",
        padding: "2px 12px",
        textAlign: "center",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      💬 {t("MyLikes.buttons.chat")}
    </button>
  )}
</span>
          </div>
        </div>

        {/* RIGHT: location + date + buttons — same layout as Gallery */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            fontSize: 13,
          }}
        >
          {/* location + date */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "#555",
              whiteSpace: "nowrap",
            }}
          >
            {locationLabel && (
  <button
    onClick={() => {
      const city = post.location?.city?.trim();
      const country = post.location?.country?.trim();
      const countryCode = post.location?.countryCode?.trim();

      if (!city || !(countryCode || country)) return;

      navigate(
        `/location-collection?city=${encodeURIComponent(city)}&country=${encodeURIComponent(
          country || ""
        )}&countryCode=${encodeURIComponent(countryCode || "")}`
      );
    }}
    style={{
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      color: "#555",
      cursor: "pointer",
      fontSize: 13,
      whiteSpace: "nowrap",
    }}
    title={locationLabel}
  >
    📍 {locationLabel}
  </button>
)}
            <span>{dateLabel}</span>
          </div>

          {/* buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => openPresentation(post._id)}
              style={{
                backgroundColor: "#111",
                color: "#bbb",
                border: "1px solid #ccc",
                borderRadius: "6px",
                padding: "3px 4.5px",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              🎬 {t("MyLikes.post.presentationButton")}
            </button>

            <button
              onClick={() =>
                post.likedByUser ? onUnlike(post._id) : onLike(post._id)
              }
              style={{
                backgroundColor: post.likedByUser ? "#ffccd5" : "#111",
                color: post.likedByUser ? "#a00" : "#bbb",
                border: "1px solid #ccc",
                borderRadius: "6px",
                padding: "3px 5px",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              {post.likedByUser
                ? t("MyLikes.post.unlikeButton", { count: post.likes })
                : t("MyLikes.post.likeButton", { count: post.likes })}
            </button>
          </div>
        </div>
      </div>

      {/* MEDIA AREA — horizontal row, same-sized tiles as Gallery */}
      <div ref={containerRef} style={mediaRowContainerStyle}>
        {post.media.length === 0 ? (
          <p style={{ color: "#bbb" }}>{t("MyLikes.post.noMedia")}</p>
        ) : (
          post.media.map((m, index) => (
            <MediaItem
              key={`${m.videoId ?? m.url}-${index}`}
              postId={post._id}
              registerChild={registerChild}
              isActive={isActive && index === currentIdx}
              isPrepared={isActive || inWindow}
              index={index}
              media={m}
              globalMuted={globalMuted}
              openViewer={(i) => openViewer(post.media, i)}
              hoveredKey={hoveredKey}
              handleHoverEnter={handleHoverEnter}
              handleHoverLeave={handleHoverLeave}
              onMusicClick={onMusicClick}
            />
          ))
        )}
      </div>
    </section>
  );    
}

export const PostSection = React.memo(
  PostSectionComponent,
  (prev, next) => {
    return (
      prev.post._id === next.post._id &&
      prev.isActive === next.isActive &&
      prev.inWindow === next.inWindow &&
      prev.globalMuted === next.globalMuted
    );
  }
);

export default {
  useMediaOrientation,
  ImageAutoFit,
  MediaItem,
  PostSection,
};
